import {ApiClient} from '@hconnect/apiclient'
import {useQuery} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import moment, {Moment} from 'moment-timezone'

import {
  type AmpelPriceLevels,
  computePriceLevelsByQuantile,
  getPricePerHour,
  type PricePerHour
} from '../ampel'
import type {ElectricityPrice, ElectricityPriceForecastData} from '../api'
import {getElectricityPricePath} from '../api/electricity.endpoints'
import {mergeActualAndForecast} from '../selectors'

export const ELECTRICITY_PRICES_DATA_QUERY_KEY = 'electricityPrice'

type MomentRange = [start: Moment, end: Moment]

export const electricityPricesDataQueryOptions = (
  plantId: string,
  range: MomentRange,
  timezoneId: string
) => {
  const [from, to] = range

  return {
    queryKey: [
      ELECTRICITY_PRICES_DATA_QUERY_KEY,
      {
        plantCode: plantId,
        from: from.toISOString(),
        to: to.toISOString(),
        timezoneId
      }
    ] as const,
    queryFn: async ({signal}: {signal?: AbortSignal}) => {
      const response = await ApiClient.axiosInstance.get<ElectricityPrice>(
        getElectricityPricePath(plantId),
        {
          params: {time_from: from.toISOString(), time_to: to.toISOString()},
          signal
        }
      )

      return response.data
    }
  }
}

interface ElectricityPricesData extends ElectricityPriceForecastData {
  priceLevels: AmpelPriceLevels
  pricePerHour: PricePerHour
}

interface UseElectricityPricesDataQueryParams {
  plantId: string
  timezoneId: string
  range: MomentRange
  enabled?: boolean
  throwOnError?: boolean
}

/**
 * hook to provide electricity prices data used in planning charts
 */
export const useElectricityPricesDataQuery = ({
  plantId,
  timezoneId,
  range,
  enabled = true,
  throwOnError = true
}: UseElectricityPricesDataQueryParams) => {
  const [start, end] = range

  return useQuery<ElectricityPrice, AxiosError, ElectricityPricesData>({
    ...electricityPricesDataQueryOptions(plantId, range, timezoneId),
    enabled: enabled && Boolean(plantId),
    throwOnError,
    select: (electricityPrices) => {
      const {currency} = electricityPrices
      const actual = electricityPrices.actual.map((entry) => ({
        datetime: moment.utc(entry.datetime),
        value: entry.value
      }))
      const forecast = electricityPrices.forecast.map((entry) => ({
        datetime: moment.utc(entry.datetime),
        value: entry.value
      }))
      const mergedPrices = mergeActualAndForecast({
        actual,
        forecast,
        startDatetime: start,
        endDatetime: end,
        timezoneId
      })
      const priceLevels = computePriceLevelsByQuantile(mergedPrices)
      const pricePerHour = getPricePerHour(mergedPrices, priceLevels)

      return {
        currency,
        actual,
        forecast,
        mergedPrices,
        priceLevels,
        pricePerHour
      }
    }
  })
}
